<template>
  <div>
    <!-- Screening Completed Display -->
    <div v-if="screening_schedule && (screening_schedule.status === 'forwarded_to_director' || screeningCompleted)" class="card border-0 shadow-sm p-4 mb-4">
      <h5 class="mb-4 text-success font-weight-bold">
        {{ screeningCompletedStatus }}
      </h5>

      <!-- Screening Officer -->
      <div v-if="screening_schedule.signature" class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="text-muted mb-0">Screening Officer</h6>
          <button 
            type="button"
            v-if="!editOfficerMode" 
            @click="enableOfficerEdit" 
            class="btn btn-sm btn-outline-primary">
            <i class="fas fa-edit"></i> Update
          </button>
        </div>

        <!-- View Mode -->
        <div v-if="!editOfficerMode">
          <p class="mb-1"><strong>Date:</strong> {{ dateFormatter(screening_schedule.completed_at) }}</p>
          <div class="signature-box">
            <img
                :src="screening_schedule.signature"
                alt="Screening Officer Signature"
            />
          </div>
          <div v-if="screening_schedule.remarks && screening_schedule.remarks.screening_remarks">
            <h6 class="mt-2 text-muted">Remarks</h6>
            <p class="pl-1 mb-0">{{ screening_schedule.remarks.screening_remarks }}</p>
          </div>
        </div>

        <!-- Edit Mode -->
        <div v-else class="border rounded p-3 bg-light">
          <div class="form-group">
            <label class="font-weight-bold">Completion Date</label>
            <app-input type="date" v-model="screening_schedule.completed_at" placeholder="Select Date" />
          </div>

          <div class="form-group">
            <label class="font-weight-bold">Screening Officer Remarks</label>
            <textarea
                class="form-control"
                v-model="screeningRemarks.screening_remarks"
                rows="3"
                placeholder="Enter remarks here..."
            ></textarea>
          </div>

          <div class="form-group">
            <div class="signature-pad-wrapper">
              <label class="font-weight-bold signature-label">Screening Officer Signature</label>
              <div class="signature-pad-container">
                <VueSignaturePad
                    id="signature-officer-edit"
                    width="220px"
                    height="150px"
                    class="border rounded bg-white"
                    ref="signaturePadOfficerEdit"
                    :options="{ onBegin: () => $refs.signaturePadOfficerEdit.resizeCanvas() }"
                />
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end">
            <button type="button" @click="cancelOfficerEdit" class="btn btn-sm btn-outline-danger mr-2">Cancel</button>
            <button type="button" @click="undoOfficerEdit" class="btn btn-sm btn-outline-warning mr-2">Undo</button>
            <button type="button" @click="updateOfficerData" class="btn btn-sm btn-success">Save Changes</button>
          </div>
        </div>
      </div>

      <!-- Director -->
      <div v-if="screeningCompleted && screening_schedule.director_signature">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="text-muted mb-0">Director</h6>
          <button 
            type="button"
            v-if="!editDirectorMode" 
            @click="enableDirectorEdit" 
            class="btn btn-sm btn-outline-primary">
            <i class="fas fa-edit"></i> Update
          </button>
        </div>

        <!-- View Mode -->
        <div v-if="!editDirectorMode">
          <p class="mb-1"><strong>Date:</strong> {{ dateFormatter(screening_schedule.director_signature_date) }}</p>
          <div class="signature-box">
            <img
                :src="screening_schedule.director_signature"
                alt="Director Signature"
            />
          </div>
          <div v-if="screening_schedule.remarks && screening_schedule.remarks.final_remarks">
            <h6 class="mt-2 text-muted">Final Remarks</h6>
            <p class="pl-1 mb-0">{{ screening_schedule.remarks.final_remarks }}</p>
          </div>
        </div>

        <!-- Edit Mode -->
        <div v-else class="border rounded p-3 bg-light">
          <div class="form-group">
            <label class="font-weight-bold">Director Signature Date</label>
            <app-input
                type="date"
                v-model="screening_schedule.director_signature_date"
                placeholder="Select Date"
            />
          </div>

          <div class="form-group">
            <label class="font-weight-bold">Final Remarks</label>
            <textarea
                class="form-control"
                v-model="screeningRemarks.final_remarks"
                rows="3"
                placeholder="Enter final remarks here..."
            ></textarea>
          </div>

          <div class="form-group">
            <div class="signature-pad-wrapper">
              <label class="font-weight-bold signature-label">Director Signature</label>
              <div class="signature-pad-container">
                <VueSignaturePad
                    id="signature-director-edit"
                    width="220px"
                    height="150px"
                    class="border rounded bg-white"
                    ref="signaturePadDirectorEdit"
                    :options="{ onBegin: () => $refs.signaturePadDirectorEdit.resizeCanvas() }"
                />
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end">
            <button type="button" @click="cancelDirectorEdit" class="btn btn-sm btn-outline-danger mr-2">Cancel</button>
            <button type="button" @click="undoDirectorEdit" class="btn btn-sm btn-outline-warning mr-2">Undo</button>
            <button type="button" @click="updateDirectorData" class="btn btn-sm btn-success">Save Changes</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Profile in Review Message -->
    <div v-else-if="!screening_schedule">
      <h6 class="text-center text-danger bg-light p-3">Profile is currently under review.</h6>
    </div>

    <!-- Screening Completion Section -->
    <div v-if="vettingCompleteModalShow || (assignedToCurrentUser && !assignToDirectorModal && !screeningCompleted)" class="card card-with-search card-with-shadow p-4 mb-4">
      <div class="row">
        <!-- Completion Date -->
        <div class="form-group col-md-6">
          <label class="font-weight-bold">Completion Date</label>
          <app-input type="date" v-model="screening_schedule.completed_at" placeholder="Select Date" />
        </div>

        <!-- Screening Officer Remarks -->
        <div class="form-group col-md-12 mt-3">
          <label class="font-weight-bold">Screening Officer Remarks</label>
          <textarea
              class="form-control"
              v-model="screeningRemarks.screening_remarks"
              rows="3"
              placeholder="Enter remarks here..."
          ></textarea>
        </div>

        <!-- Officer Signature -->
        <div class="form-group col-md-3">
          <label class="font-weight-bold">Screening Officer Signature</label>
          <VueSignaturePad
              id="signature-officer"
              width="100%"
              height="150px"
              class="border rounded"
              ref="signaturePad"
              :options="{ onBegin: () => $refs.signaturePad.resizeCanvas() }"
          />
        </div>

        <!-- Director Assignment -->
        <div class="form-group col-md-12">
          <label class="font-weight-bold">{{ $t('Send assessment to director') }}</label>
          <app-input
              type="select"
              :placeholder="$t('select_user')"
              :list="users"
              v-model="select_user"
              :required="true"
          />
        </div>

        <!-- Action Buttons -->
        <div class="col-12 d-flex justify-content-end mt-3">
          <button @click.prevent="undo" class="btn btn-outline-danger mr-2">Undo</button>
          <button @click.prevent="submit" class="btn btn-success">Save</button>
        </div>
      </div>
    </div>

    <!-- Director Signature Section -->
    <div v-if="assignToDirectorModal" class="card card-with-search card-with-shadow p-4 mb-4">
      <div class="row">
        <!-- Director Signature Date -->
        <div class="form-group col-md-4">
          <label class="font-weight-bold">Director Signature Date</label>
          <app-input
              type="date"
              v-model="screening_schedule.director_signature_date"
              placeholder="Select Date"
          />
        </div>

        <!-- Final Remarks Input -->
        <div class="form-group col-md-12 mt-3" >
          <label class="font-weight-bold">Final Remarks</label>
          <textarea
              class="form-control"
              v-model="screeningRemarks.final_remarks"
              rows="3"
              placeholder="Enter final remarks here..."
          ></textarea>
        </div>

        <!-- Director Signature Pad -->
        <div class="form-group col-md-3">
          <label class="font-weight-bold">Director Signature</label>
          <VueSignaturePad
              id="signature-director"
              width="100%"
              height="150px"
              class="border rounded"
              ref="signaturePad"
              :options="{ onBegin: () => $refs.signaturePad.resizeCanvas() }"
          />
        </div>

        <!-- Action Buttons -->
        <div class="col-12 d-flex justify-content-end mt-3">
          <button @click.prevent="undo" class="btn btn-outline-danger mr-2">Undo</button>
          <button @click.prevent="saveDirector" class="btn btn-success">Save</button>
        </div>
      </div>
    </div>

    <!-- Notice if Not Assigned to Current User -->
    <div v-if="!assignedToCurrentUser && screening_schedule" class="card border-0 shadow-sm p-4 mb-3 bg-light">
      <h5 class="text-secondary font-weight-bold mb-2">Assignment Notice</h5>
      <p class="mb-0">
        This screening has been assigned to an internal user. Please wait until they complete their review.
      </p>
    </div>
  </div>
</template>


<script>
import {FormMixin} from "@/core/mixins/form/FormMixin";
import CoreLibrary from "@/core/helpers/CoreLibrary";
import {Candidate} from "@Views/Screening/utility/apiAgent";
import {axiosGet} from "@/app/Helpers/AxiosHelper";
import {GET_COMPANY_USERS} from "@/app/Config/ApiUrl";
import VueSignaturePad from "vue-signature-pad";
import {dateFormatter} from '@Views/Screening/utility/common';


export default {
  name: "ScreeningRemarks",
  mixins: [FormMixin],
  extends: CoreLibrary,
  components: {
    VueSignaturePad
  },
  props: {
    candidateId: Number,
    companyId: Number,
    applicantFiles: Array,
    screening_schedule: Object
  },
  data(){
    return {
      select_user: '',
      users: [],
      screeningRemarks:{
        screening_remarks: '',
        final_remarks: '',
      },
      editOfficerMode: false,
      editDirectorMode: false,

    }
  },
  mounted(){
    this.getUsers();
    this.select_user = this.screening_schedule?.director_id || '';
    if (this.screening_schedule.remarks?.screening_remarks){
      this.screeningRemarks.screening_remarks = this.screening_schedule.remarks.screening_remarks
    }
    if (this.screening_schedule.remarks?.final_remarks){
      this.screeningRemarks.final_remarks = this.screening_schedule.remarks.final_remarks
    }
  },
  created(){
    this.$hub.$emit("candidateDetailActiveIndex", 11);

  },
  watch: {
    select_user(directorId) {
      if (this.screening_schedule && directorId) {
        this.screening_schedule.director_id = directorId;
      }
    },
  },
  computed: {
    vettingCompleteModalShow(){
      return this.screening_schedule && this.screening_schedule.status === 'in_progress'
    },
    assignToDirectorModal(){
      return this.screening_schedule && this.screening_schedule.status === 'forwarded_to_director'

    },
    screeningCompleted(){
      return this.screening_schedule && this.screening_schedule.status === 'completed'

    },
    assignedToCurrentUser(){
      return this.screening_schedule && this.screening_schedule.assigned_to === this.user.id
    },
    screeningCompletedStatus(){
      return this.screeningCompleted ? '✅ Screening Completed' : '🕒 Screening In Progress'
    },
    user() {
      return this.$store.state.user.loggedInUser;
    },
    assignedDirectorId() {
      return this.select_user || this.screening_schedule?.director_id || '';
    },
  },
  methods: {
    dateFormatter,
    undo() {
      this.$refs.signaturePad.undoSignature();
    },
    saveDirector() {
      const {isEmpty, data} = this.$refs.signaturePad.saveSignature();
      const directorId = this.assignedDirectorId;
      const body = {
        director_signature: data,
        director_signature_date: this.screening_schedule.director_signature_date,
        status: 'completed',
        remarks: this.screeningRemarks,
      };
      if (directorId) {
        body.director_id = directorId;
      }
      Candidate.postVetting(this.screening_schedule.id, body)
          .then((res) => {
            this.$toastr.s(res.data.message);
            location.reload();
          })
          .catch((error) => {
            console.error(error);
          })
    },
    submit() {
      const {isEmpty, data} = this.$refs.signaturePad.saveSignature();
      const directorId = this.assignedDirectorId;
      if (!directorId) {
        this.$toastr.e('Please select a director');
        return;
      }
      this.screening_schedule.director_id = directorId;
      const body = {
        signature: data,
        completed_at: this.screening_schedule.completed_at,
        director_id: directorId,
        status: 'forwarded_to_director',
        remarks: this.screeningRemarks,

      };
      Candidate.postVetting(this.screening_schedule.id, body)
          .then((res) => {
            this.$toastr.s(res.data.message);
             location.reload();
          })
          .catch((error) => {
            // console.error(error);
          })

    },

    getUsers() {
      axiosGet(GET_COMPANY_USERS)
          .then((res) => {
            this.users = res.data;
            if (!this.select_user && this.users.length && !this.assignToDirectorModal && !this.screeningCompleted) {
              this.select_user = this.users[0].id;
            }
          })
          .catch((error) => {

          });
    },
    assignToUser(){
      const body = {
        assigned_to: this.select_user,
        status: 'forwarded_to_user'
      };
      Candidate.postVetting(this.screening_schedule.id, body)
          .then((res) => {
            this.$toastr.s(res.data.message);
          })
          .catch((error) => {
            console.error(error);
            this.$toastr.s(error.data.message);

          })
          .finally(() => {
            this.closeModal(this.assignToUserModalId);
          });

    },

    // Officer Edit Methods
    enableOfficerEdit() {
      this.editOfficerMode = true;
      this.$nextTick(() => {
        if (this.screening_schedule.signature) {
          this.$refs.signaturePadOfficerEdit.fromDataURL(this.screening_schedule.signature);
        }
      });
    },
    cancelOfficerEdit() {
      this.editOfficerMode = false;
      // Reset to original values
      if (this.screening_schedule.remarks?.screening_remarks) {
        this.screeningRemarks.screening_remarks = this.screening_schedule.remarks.screening_remarks;
      }
    },
    undoOfficerEdit() {
      this.$refs.signaturePadOfficerEdit.undoSignature();
    },
    updateOfficerData() {
      const {isEmpty, data} = this.$refs.signaturePadOfficerEdit.saveSignature();
      const body = {
        signature: data,
        completed_at: this.screening_schedule.completed_at,
        remarks: this.screeningRemarks,
      };
      Candidate.updateVetting(this.screening_schedule.id, body)
          .then((res) => {
            this.$toastr.s(res.data.message);
            this.editOfficerMode = false;
            location.reload();
          })
          .catch((error) => {
            console.error(error);
            this.$toastr.e('Failed to update officer data');
          });
    },

    // Director Edit Methods
    enableDirectorEdit() {
      this.editDirectorMode = true;
      this.$nextTick(() => {
        if (this.screening_schedule.director_signature) {
          this.$refs.signaturePadDirectorEdit.fromDataURL(this.screening_schedule.director_signature);
        }
      });
    },
    cancelDirectorEdit() {
      this.editDirectorMode = false;
      // Reset to original values
      if (this.screening_schedule.remarks?.final_remarks) {
        this.screeningRemarks.final_remarks = this.screening_schedule.remarks.final_remarks;
      }
    },
    undoDirectorEdit() {
      this.$refs.signaturePadDirectorEdit.undoSignature();
    },
    updateDirectorData() {
      const {isEmpty, data} = this.$refs.signaturePadDirectorEdit.saveSignature();
      const directorId = this.assignedDirectorId;
      const body = {
        director_signature: data,
        director_signature_date: this.screening_schedule.director_signature_date,
        remarks: this.screeningRemarks,
      };
      if (directorId) {
        body.director_id = directorId;
      }
      Candidate.updateVetting(this.screening_schedule.id, body)
          .then((res) => {
            this.$toastr.s(res.data.message);
            this.editDirectorMode = false;
            location.reload();
          })
          .catch((error) => {
            console.error(error);
            this.$toastr.e('Failed to update director data');
          });
    },
  }
};
</script>

<style scoped>
.checklist-item {
  background: #f9f9f9;
  border-radius: 6px;
}
.checklist-item label {
  font-weight: 500;
  text-align: left;
}
.checklist-item .form-group {
  margin-bottom: 0.75rem;
}

/* Signature Box - Fixed Size for View Mode */
.signature-box {
  width: 150px;
  height: 80px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 5px;
  background: #fff;
  display: inline-block;
  margin-bottom: 10px;
}

.signature-box img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

/* Signature Pad Wrapper - Centers label and pad */
.signature-pad-wrapper {
  display: flex;
  align-items: center;
  gap: 15px;
}

.signature-label {
  margin-bottom: 0;
  white-space: nowrap;
}

/* Signature Pad Container - Fixed Size for Edit Mode */
.signature-pad-container {
  display: inline-block;
  max-width: 220px;
}
</style>
